import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { HistologyImage, PracticeSession, ImageAnnotation } from '../types';
import { Button } from '../components/ui/Button';
import { generateId, isFuzzyMatch } from '../lib/utils';
import { Check, X, ArrowRight, HelpCircle, Info, Microscope, RefreshCcw, MapPin, Navigation } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface QuizQuestion {
  id: string;
  type: 'tissue' | 'annotation';
  image: HistologyImage;
  annotation?: ImageAnnotation;
  target: string;
}

const PracticePage = () => {
  const { pdfId } = useParams();
  const [searchParams] = useSearchParams();
  const customIds = searchParams.get('ids')?.split(',');
  
  const navigate = useNavigate();
  const { images, user, saveSession } = useAppStore();
  
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [sessionStartTime] = useState(new Date().toISOString());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let selectedImages = [];
    if (customIds && customIds.length > 0) {
      selectedImages = images.filter(img => customIds.includes(img.id));
    } else if (pdfId) {
      selectedImages = images.filter(img => img.pdf_id === pdfId);
    } else {
      selectedImages = [...images].sort(() => 0.5 - Math.random()).slice(0, 10);
    }

    // Generate a comprehensive question list: 1 Tissue ID + N Annotation IDs per slide
    const qList: QuizQuestion[] = [];
    selectedImages.forEach(img => {
      // 1. Add the main tissue identification question
      qList.push({
        id: generateId(),
        type: 'tissue',
        image: img,
        target: img.identified_organ_tissue || 'Unknown'
      });

      // 2. Add each annotation as a separate question
      if (img.annotations && img.annotations.length > 0) {
        img.annotations.forEach(ann => {
          qList.push({
            id: generateId(),
            type: 'annotation',
            image: img,
            annotation: ann,
            target: ann.label
          });
        });
      }
    });

    // Shuffle the entire pool of questions for true active recall challenge
    setQuestions(qList.sort(() => 0.5 - Math.random()));
  }, [pdfId, images, customIds]);

  const currentQuestion = questions[currentIndex];

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!currentQuestion || feedback) return;

    const isCorrect = isFuzzyMatch(userInput, currentQuestion.target);
    
    if (isCorrect) {
      setFeedback('correct');
      setScore(s => s + 1);
    } else {
      setFeedback('incorrect');
    }
  };

  const handleRetry = () => {
    setFeedback(null);
    setUserInput('');
    setTimeout(() => {
        inputRef.current?.focus();
    }, 50);
  };

  const handleNext = () => {
    setFeedback(null);
    setUserInput('');
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      finishSession();
    }
  };

  const finishSession = () => {
    const finalScore = score + (feedback === 'correct' ? 1 : 0);
    const session: PracticeSession = {
      id: generateId(),
      user_id: user?.id || 'guest',
      started_at: sessionStartTime,
      completed_at: new Date().toISOString(),
      total_questions: questions.length,
      correct_answers: finalScore,
      score_percentage: Math.round((finalScore / questions.length) * 100),
      pdf_document_id: pdfId
    };
    saveSession(session);
    setShowResult(true);
  };

  // Clinical Arrow Component
  const ClinicalArrow = ({ color = "#3B82F6", size = 48 }: { color?: string, size?: number }) => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))' }}
    >
      <path d="M7 17L17 7M17 7H7M17 7V17" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 17L17 7M17 7H7M17 7V17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <HelpCircle className="h-12 w-12 text-slate-300 mb-4" />
        <h3 className="text-xl font-bold text-slate-900">No images available</h3>
        <p className="text-slate-500 mb-6">Upload a PDF or select slides from your library to start practicing.</p>
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate('/library')}>Library</Button>
          <Button onClick={() => navigate('/upload')}>Upload PDF</Button>
        </div>
      </div>
    );
  }

  if (showResult) {
    const finalPercent = Math.round((score / questions.length) * 100);
    return (
      <div className="max-w-md mx-auto mt-10 p-10 bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 text-center">
        <div className="bg-primary-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-inner">
            <Check className="h-10 w-10 text-primary-500" strokeWidth={3} />
        </div>
        <h2 className="text-3xl font-black text-slate-900 mb-2 tracking-tight uppercase">Session Done</h2>
        <div className="text-6xl font-black text-primary-500 my-8">
            {finalPercent}%
        </div>
        <p className="text-slate-500 font-medium mb-10 leading-relaxed">
          You identified <span className="font-bold text-slate-900">{score}</span> out of <span className="font-bold text-slate-900">{questions.length}</span> histology targets correctly.
        </p>
        <div className="flex flex-col gap-3">
            <Button onClick={() => window.location.reload()} className="h-14 rounded-2xl shadow-xl shadow-primary-500/20 font-black uppercase text-xs tracking-widest">
                Retake Session
            </Button>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="font-bold text-slate-400 uppercase text-[10px] tracking-widest">
                Return to Dashboard
            </Button>
        </div>
      </div>
    );
  }

  const filterStyle = currentQuestion.image.filters ? {
    filter: `brightness(${currentQuestion.image.filters.brightness}%) contrast(${currentQuestion.image.filters.contrast}%) grayscale(${currentQuestion.image.filters.grayscale ? 1 : 0}) invert(${currentQuestion.image.filters.inverted ? 1 : 0})`
  } : {};

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
            <Microscope className="h-5 w-5 text-primary-500" />
            Active Recall
        </h2>
        <div className="text-sm font-black text-slate-400 bg-white border border-slate-200 px-4 py-1.5 rounded-full shadow-sm">
            TARGET {currentIndex + 1} OF {questions.length}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Image Display */}
        <div className="lg:col-span-3 bg-slate-950 rounded-3xl overflow-hidden aspect-square md:aspect-[4/3] flex items-center justify-center relative shadow-2xl group border-4 border-white ring-1 ring-slate-200">
            <AnimatePresence mode="wait">
                <motion.div 
                   key={currentQuestion.id}
                   initial={{ opacity: 0, scale: 0.95 }}
                   animate={{ opacity: 1, scale: 1 }}
                   exit={{ opacity: 0, scale: 1.05 }}
                   className="w-full h-full relative"
                >
                    <img 
                        src={currentQuestion.image.image_url}
                        alt="Histology Slide"
                        style={filterStyle}
                        className="w-full h-full object-contain"
                    />

                    {/* Clinical Arrow highlighting the specific structure to identify */}
                    {currentQuestion.type === 'annotation' && currentQuestion.annotation && (
                      <div 
                        className="absolute z-20 pointer-events-none" 
                        style={{ left: `${currentQuestion.annotation.x_percent}%`, top: `${currentQuestion.annotation.y_percent}%` }}
                      >
                        <div className="relative -translate-x-[10%] -translate-y-[90%]">
                           <ClinicalArrow color="#3B82F6" size={56} />
                        </div>
                      </div>
                    )}
                </motion.div>
            </AnimatePresence>
            
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <div className="bg-slate-950/40 backdrop-blur-md px-3 py-1 rounded-lg text-[10px] text-white font-bold uppercase tracking-widest border border-white/10">
                Pattern Analysis
              </div>
              {currentQuestion.type === 'annotation' && (
                <div className="bg-primary-600 px-3 py-1 rounded-lg text-[10px] text-white font-black uppercase tracking-widest border border-white/20 shadow-lg animate-bounce">
                  Identify Targeted Structure
                </div>
              )}
            </div>
        </div>

        {/* Interaction Panel */}
        <div className="lg:col-span-2 flex flex-col justify-start space-y-6">
            {!feedback ? (
                <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
                    <div className="space-y-4">
                        <div className="space-y-1">
                          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest ml-1">
                            {currentQuestion.type === 'annotation' ? "Target: Structure at Arrow" : "Target: Tissue/Organ Identification"}
                          </label>
                          <p className="text-[10px] text-slate-400 font-medium ml-1">
                            {currentQuestion.type === 'annotation' ? "Identify the anatomical feature at the arrow's tip." : "Examine the morphology and classify the overall tissue."}
                          </p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <input
                                ref={inputRef}
                                type="text"
                                autoFocus
                                className="w-full text-xl px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-primary-500 focus:bg-white focus:ring-4 focus:ring-primary-500/10 outline-none transition-all font-bold placeholder:text-slate-300"
                                placeholder="Diagnosis..."
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                            />
                            <Button type="submit" size="lg" className="w-full h-14 rounded-2xl shadow-xl shadow-primary-500/20 font-black uppercase text-xs tracking-widest" disabled={!userInput.trim()}>
                                Confirm Diagnosis
                            </Button>
                        </form>
                    </div>
                    
                    <button type="button" onClick={() => { setFeedback('incorrect'); setUserInput(currentQuestion.target); }} className="w-full text-xs font-bold text-slate-400 hover:text-primary-500 transition-colors py-2 uppercase tracking-widest">
                        Reveal Identification
                    </button>
                </div>
            ) : (
                <div className="space-y-6">
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl"
                  >
                      <div className={`flex items-center mb-6 ${feedback === 'correct' ? 'text-emerald-600' : 'text-red-500'}`}>
                          {feedback === 'correct' ? <div className="p-2.5 bg-emerald-50 rounded-xl mr-3 shadow-sm"><Check className="h-6 w-6" strokeWidth={3} /></div> : <div className="p-2.5 bg-red-50 rounded-xl mr-3 shadow-sm"><X className="h-6 w-6" strokeWidth={3} /></div>}
                          <span className="text-2xl font-black uppercase tracking-tight">{feedback === 'correct' ? 'Correct' : 'Incorrect'}</span>
                      </div>
                      
                      <div className="mb-8">
                          <p className="text-[10px] text-slate-400 uppercase tracking-[0.2em] font-black mb-2">Scientific Result</p>
                          <p className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
                            {currentQuestion.target}
                          </p>
                          {currentQuestion.type === 'annotation' && (
                            <p className="mt-3 text-xs font-bold text-primary-600 uppercase tracking-widest">
                              (Found in: {currentQuestion.image.identified_organ_tissue})
                            </p>
                          )}
                      </div>

                      <div className="space-y-3">
                          {feedback === 'incorrect' && (
                            <Button variant="outline" onClick={handleRetry} className="w-full h-14 rounded-2xl font-black uppercase text-xs tracking-widest border-2 border-slate-100 bg-white hover:bg-slate-50 mb-2">
                                <RefreshCcw className="mr-2 h-4 w-4" /> Try Again
                            </Button>
                          )}
                          <Button onClick={handleNext} className="w-full h-14 rounded-2xl shadow-lg shadow-primary-500/20 font-black uppercase text-xs tracking-widest">
                              {currentIndex < questions.length - 1 ? 'Next Target' : 'Finish Session'} <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                      </div>
                  </motion.div>
                </div>
            )}

            <div className="bg-slate-50 border border-slate-200 p-6 rounded-3xl flex items-start gap-4">
              <div className="p-2.5 bg-white rounded-2xl shadow-sm border border-slate-100"><Info className="h-5 w-5 text-primary-500" /></div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-900 uppercase tracking-[0.2em]">Context Clue</p>
                <p className="text-xs text-slate-500 leading-relaxed font-medium">
                  {currentQuestion.type === 'annotation' 
                    ? "Structure arrows focus on specific cells or membranes. Look for polarity, shape, and nuclear localization." 
                    : "For tissue classification, evaluate the repeating layers or organizational subunits like follicles or lobules."}
                </p>
              </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default PracticePage;